import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { useSettingsContext } from "../../context/SettingsContext";

const Terms: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsSettingsOpen, setActiveSettingsTab } = useSettingsContext();

  // Close settings when navigating to /terms (after route change to prevent flash)
  useEffect(() => {
    if (location.pathname === "/terms") {
      setIsSettingsOpen(false);
    }
  }, [location.pathname, setIsSettingsOpen]);

  const handleBackToSettings = () => {
    // Navigate with state to indicate we want to open settings
    navigate("/", { state: { openSettings: true, settingsTab: "profile" } });
  };

  return (
    <div className="min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark flex flex-col">
      {/* Simple Header */}
      <header className="border-b border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
        <div className="container mx-auto px-6 py-8 max-w-6xl">
          <button
            onClick={handleBackToSettings}
            className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <span className="font-medium">Back to Settings</span>
          </button>
          <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2">
            Terms and Conditions
          </h1>
          <p className="text-base text-text-secondary dark:text-text-secondaryDark">
            Please review our terms to understand how {AppConfig.PRODUCT_NAME_SHORT} operates and governs the use of its services.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="space-y-16">
          {/* 1. ACCEPTANCE OF TERMS */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">1</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Acceptance of Terms
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.1 Agreement to Terms
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  By accessing or using the {AppConfig.PRODUCT_NAME_SHORT} platform ("Service", "Platform", "we", "us", "our"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.2 Modifications
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  We reserve the right to modify these Terms at any time. Material changes will be notified via email or platform notification. Continued use after changes constitutes acceptance.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.3 Age Requirements
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  You must be at least 18 years old or the age of majority in your jurisdiction to use the Service.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.4 Account Responsibility
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
                </p>
              </div>
            </div>
          </section>

          {/* 2. SERVICE DESCRIPTION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">2</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Service Description
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.1 Platform Overview
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  {AppConfig.PRODUCT_NAME_SHORT} is a RevOps (Revenue Operations) automation platform that provides:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Multi-tenant organization management with role-based access control",
                    "Agent marketplace for installing and managing automation agents",
                    "Integration platform connecting third-party services (HubSpot, Stripe, Asana, Slack)",
                    "AI-powered features using Large Language Models (LLMs) for intelligent automation",
                    "Data synchronization between connected services"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.2 RevSync Agent
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  The RevSync agent is an automated workflow that:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Syncs subscription data between HubSpot and Stripe",
                    "Creates contracts from closed-won deals",
                    "Links Stripe customers to HubSpot contacts/companies",
                    "Processes webhooks from both services",
                    "Performs daily reconciliation of subscription data"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.3 Service Availability
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "We strive for 99.9% uptime but do not guarantee uninterrupted service",
                    "Scheduled maintenance will be announced in advance when possible",
                    "We are not liable for downtime due to third-party service outages"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 3. USER ACCOUNTS AND ORGANIZATIONS */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">3</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                User Accounts and Organizations
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.1 Account Registration
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "You must provide accurate, current, and complete information",
                    "You must maintain and update your information as needed",
                    "One person or entity may not maintain multiple accounts to circumvent usage limits"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.2 Organizations
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Users can create or join organizations",
                    "Organization owners have full control over their organization",
                    "Organization admins can manage members and settings",
                    "Members have view-only access unless granted additional permissions"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.3 Role-Based Access
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    { label: "Owner", desc: "Full control, can transfer ownership, delete organization" },
                    { label: "Admin", desc: "Can manage members, configure agents, cannot uninstall agents or transfer ownership" },
                    { label: "Member", desc: "View-only access to agents and data" },
                    { label: "SuperAdmin", desc: `Platform-level administrators (assigned by ${AppConfig.PRODUCT_NAME_SHORT})` }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">
                        <strong className="text-text-primary dark:text-text-primaryDark">{item.label}:</strong> {item.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.4 Account Security
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "You are responsible for maintaining account security",
                    "Notify us immediately of unauthorized access",
                    "We implement security measures but cannot guarantee absolute security"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 4. THIRD-PARTY INTEGRATIONS */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">4</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Third-Party Integrations
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.1 Integration Services
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  Our platform integrates with:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    { name: "Stripe", desc: "Payment processing and subscription management" },
                    { name: "HubSpot", desc: "CRM, deals, contacts, companies, and custom objects" },
                    { name: "Asana", desc: "Project and task management" },
                    { name: "Slack", desc: "Team communication and notifications" },
                    { name: "Other services", desc: "As added to the platform" }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">
                        <strong className="text-text-primary dark:text-text-primaryDark">{item.name}:</strong> {item.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.2 Third-Party Terms
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Your use of integrations is subject to the third-party's terms of service",
                    "We are not responsible for third-party service availability or changes",
                    "You must maintain valid accounts and permissions with third-party services"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.3 OAuth Authorization
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Connecting integrations requires OAuth authorization",
                    "You grant us permission to access your third-party account data",
                    "You can revoke access at any time through the integration settings",
                    "Revoking access may disable agent functionality"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.4 RevSync Agent Responsibilities
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  When using the RevSync agent, you are responsible for:
                </p>
                <ul className="space-y-2.5 ml-4 mb-4">
                  {[
                    "Ensuring HubSpot and Stripe accounts are properly configured",
                    "Verifying deal and subscription data accuracy",
                    "Monitoring sync logs for errors",
                    "Reviewing created contracts for accuracy"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-4 border border-divider dark:border-divider-dark">
                  <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                    <strong className="text-text-primary dark:text-text-primaryDark">Our Limitations:</strong> We are not responsible for duplicate subscriptions, incorrect product mappings, data conflicts, or reconciliation delays up to 24 hours.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. DATA PROCESSING AND SYNCHRONIZATION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">5</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Data Processing and Synchronization
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  5.1 Data Sync Operations
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Agents automatically sync data between connected services",
                    "Sync operations occur based on webhooks and scheduled jobs",
                    "Sync frequency depends on agent configuration",
                    "We are not responsible for data conflicts or duplicates"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  5.2 Data Accuracy
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "You are responsible for data accuracy in source systems",
                    "We sync data as it exists in your connected services",
                    "We are not responsible for incorrect data in source systems",
                    "You should verify synced data for accuracy"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  5.3 Reconciliation
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  The RevSync agent performs periodic reconciliation to ensure data consistency. Reconciliation may create or update records. You can configure reconciliation settings through the agent interface.
                </p>
              </div>
            </div>
          </section>

          {/* 6. USER RESPONSIBILITIES */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">6</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                User Responsibilities
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.1 Acceptable Use
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  You agree not to:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Use the Service for illegal purposes",
                    "Violate any laws or regulations",
                    "Infringe on intellectual property rights",
                    "Transmit malicious code or viruses",
                    "Attempt to gain unauthorized access",
                    "Interfere with Service operation",
                    "Use automated systems to abuse the Service"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.2 Account Security
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Maintain strong passwords",
                    "Keep account credentials confidential",
                    "Notify us of security breaches",
                    "Use two-factor authentication when available"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.3 Integration Responsibility
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Maintain valid third-party service accounts",
                    "Ensure proper permissions for integrations",
                    "Monitor integration health",
                    "Review agent configurations regularly"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 7. INTELLECTUAL PROPERTY */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">7</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Intellectual Property
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  7.1 Our Intellectual Property
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  The Service, including software, design, and content, is the property of {AppConfig.PRODUCT_NAME_SHORT}. All rights, title, and interest belong to {AppConfig.PRODUCT_NAME_SHORT}. You may not copy, modify, or distribute our intellectual property.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  7.2 Your Intellectual Property
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  You retain ownership of your data. You grant us license to use your data to provide the Service and for Service improvement (anonymized).
                </p>
              </div>
            </div>
          </section>

          {/* 8. LIMITATION OF LIABILITY */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">8</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Limitation of Liability
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  8.1 Service Availability
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  We do not guarantee uninterrupted or error-free service. We are not liable for service interruptions or third-party service outages.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  8.2 Data Loss
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We implement backup and recovery procedures. We are not liable for data loss due to:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Your actions or omissions",
                    "Third-party service issues",
                    "Force majeure events",
                    "Unauthorized access"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  8.3 Agent Operations
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  We are not liable for agent actions based on your configuration. We are not liable for data created or modified by agents. You are responsible for reviewing agent operations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  8.4 Maximum Liability
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  Our total liability is limited to the amount you paid in the 12 months preceding the claim. We are not liable for indirect, incidental, or consequential damages. Some jurisdictions do not allow limitation of liability; your rights may vary.
                </p>
              </div>
            </div>
          </section>

          {/* 9. TERMINATION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">9</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Termination
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  9.1 Termination by You
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  You may terminate your account at any time. Termination requests are processed within 30 days. Data deletion follows our Data Retention Policy.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  9.2 Termination by Us
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We may terminate your account if:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "You violate these Terms",
                    "You engage in fraudulent activity",
                    "You fail to pay fees",
                    "You abuse the Service",
                    "Required by law"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 10. GENERAL PROVISIONS */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">10</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                General Provisions
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  10.1 Entire Agreement
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  These Terms constitute the entire agreement between you and {AppConfig.PRODUCT_NAME_SHORT}.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  10.2 Modifications
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  {AppConfig.PRODUCT_NAME_SHORT} reserves the right to modify these Terms and Conditions at any time. Updates will be posted on our platform, and continued use of the service constitutes acceptance of the revised terms.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  10.3 Severability
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  If any provision is found unenforceable, the remaining provisions remain in effect.
                </p>
              </div>
            </div>
          </section>

          {/* 11. CONTACT INFORMATION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">11</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Contact Information
              </h2>
            </div>
            
            <div className="ml-14">
              <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                For support, questions, or legal inquiries, please contact us at{" "}
                <a
                  href={`mailto:${AppConfig.SUPPORT_EMAIL}`}
                  className="text-secondary hover:text-secondary-hover font-medium transition-colors underline underline-offset-2"
                >
                  {AppConfig.SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 text-center border-t border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
        <div className="max-w-4xl mx-auto">
          <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-6">
            Questions? Contact{" "}
            <a
              href={`mailto:${AppConfig.SUPPORT_EMAIL}`}
              className="text-secondary hover:text-secondary-hover font-medium transition-colors underline underline-offset-2"
            >
              {AppConfig.SUPPORT_EMAIL}
            </a>
          </p>
          <button
            onClick={handleBackToSettings}
            className="inline-flex items-center px-8 py-3 bg-secondary text-white rounded-xl font-semibold shadow-lg hover:bg-secondary-hover transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-100"
          >
            Close Terms
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
